import Phaser from 'phaser';
import { GAME_WIDTH, PRESTIGE } from '../config';

/**
 * Legacy progress bar — shows how close the player is to the next Legacy point.
 * Thin, full-width bar below the top HUD with a flowing shimmer animation.
 */
export class LegacyBar {
    private scene: Phaser.Scene;
    private barBg: Phaser.GameObjects.Graphics;
    private barFill: Phaser.GameObjects.Graphics;
    private shimmer: Phaser.GameObjects.Graphics;
    private labelText: Phaser.GameObjects.Text;
    private progressText: Phaser.GameObjects.Text;

    private readonly x = 0;
    private readonly y: number;
    private readonly width = GAME_WIDTH;
    private readonly height = 12;

    private currentProgress = 0;  // 0..1 smoothly animated
    private targetProgress = 0;   // actual target
    private currentLegacy = 0;
    private totalLegacy = 0;
    private shimmerOffset = 0;

    constructor(scene: Phaser.Scene, y: number) {
        this.scene = scene;
        this.y = y;

        // Background bar
        this.barBg = scene.add.graphics().setDepth(900);

        // Fill bar
        this.barFill = scene.add.graphics().setDepth(901);

        // Shimmer overlay (animated glow that flows across the fill)
        this.shimmer = scene.add.graphics().setDepth(902);

        // Legacy count label (left side)
        this.labelText = scene.add.text(6, this.y + this.height / 2, '', {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 3,
        }).setOrigin(0, 0.5).setDepth(903);

        // Progress text (right side)
        this.progressText = scene.add.text(GAME_WIDTH - 6, this.y + this.height / 2, '', {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 3,
        }).setOrigin(1, 0.5).setDepth(903);

        this.drawBackground();
    }

    private drawBackground(): void {
        this.barBg.clear();
        // Dark translucent background
        this.barBg.fillStyle(0x111122, 0.85);
        this.barBg.fillRect(this.x, this.y, this.width, this.height);
        // Bottom border line
        this.barBg.lineStyle(1, 0x333355, 0.6);
        this.barBg.lineBetween(this.x, this.y + this.height, this.x + this.width, this.y + this.height);
    }

    /**
     * Update the bar with current gold earned this run.
     * @param goldEarnedThisRun - gold earned in the current run
     * @param totalLegacy - total legacy points earned across all prestiges
     */
    update(goldEarnedThisRun: number, totalLegacy: number, delta: number): void {
        this.totalLegacy = totalLegacy;

        // Calculate current and next legacy thresholds
        const divisor = PRESTIGE.legacyDivisor;

        if (goldEarnedThisRun <= 0) {
            this.targetProgress = 0;
            this.currentLegacy = 0;
        } else {
            // Linear formula: 1 legacy per divisor gold
            this.currentLegacy = Math.floor(goldEarnedThisRun / divisor);
            const goldIntoCurrentLevel = goldEarnedThisRun % divisor;
            this.targetProgress = goldIntoCurrentLevel / divisor;
        }

        this.targetProgress = Math.max(0, Math.min(1, this.targetProgress));

        // Smooth animation toward target
        const speed = 3; // lerp speed
        this.currentProgress += (this.targetProgress - this.currentProgress) * Math.min(1, speed * delta / 1000);

        // Animate shimmer offset
        this.shimmerOffset += delta * 0.15;
        if (this.shimmerOffset > this.width * 2) this.shimmerOffset -= this.width * 2;

        this.drawFill();
        this.updateLabels();
    }

    private drawFill(): void {
        this.barFill.clear();
        this.shimmer.clear();

        const fillWidth = this.currentProgress * this.width;
        if (fillWidth <= 0) return;

        // Main fill — gradient from deep purple to gold
        const segments = Math.ceil(fillWidth / 2);
        for (let i = 0; i < segments; i++) {
            const t = i / Math.max(1, segments - 1);
            // Purple (0x6633cc) → Gold (0xdaa520) gradient
            const r = Math.round(0x66 + (0xda - 0x66) * t);
            const g = Math.round(0x33 + (0xa5 - 0x33) * t);
            const b = Math.round(0xcc + (0x20 - 0xcc) * t);
            const color = (r << 16) | (g << 8) | b;
            this.barFill.fillStyle(color, 0.9);
            this.barFill.fillRect(this.x + i * 2, this.y + 1, 2, this.height - 2);
        }

        // Flowing shimmer effect — bright streaks that move across the fill
        const shimmerWidth = 40;
        const shimmerPos = this.shimmerOffset % (this.width + shimmerWidth * 2) - shimmerWidth;

        for (let i = 0; i < shimmerWidth; i++) {
            const sx = shimmerPos + i;
            if (sx < this.x || sx >= this.x + fillWidth) continue;

            // Triangle wave for shimmer intensity
            const shimmerT = i / shimmerWidth;
            const intensity = Math.sin(shimmerT * Math.PI) * 0.35;

            this.shimmer.fillStyle(0xffffff, intensity);
            this.shimmer.fillRect(sx, this.y + 1, 1, this.height - 2);
        }

        // Second shimmer (offset) for richer effect
        const shimmerPos2 = (this.shimmerOffset * 0.7 + this.width * 0.6) % (this.width + shimmerWidth * 2) - shimmerWidth;
        for (let i = 0; i < shimmerWidth; i++) {
            const sx = shimmerPos2 + i;
            if (sx < this.x || sx >= this.x + fillWidth) continue;
            const shimmerT = i / shimmerWidth;
            const intensity = Math.sin(shimmerT * Math.PI) * 0.2;
            this.shimmer.fillStyle(0xffffff, intensity);
            this.shimmer.fillRect(sx, this.y + 1, 1, this.height - 2);
        }

        // Top highlight line
        this.barFill.lineStyle(1, 0xffffff, 0.15);
        this.barFill.lineBetween(this.x, this.y + 1, this.x + fillWidth, this.y + 1);
    }

    private updateLabels(): void {
        // Left: show legacy count with star
        if (this.totalLegacy > 0 || this.currentLegacy > 0) {
            const runLegacy = this.currentLegacy;
            this.labelText.setText(`★ ${this.totalLegacy} Legacy${runLegacy > 0 ? `  (+${runLegacy} this run)` : ''}`);
        } else {
            this.labelText.setText('★ Legacy');
        }

        // Right: show percentage
        const pct = Math.floor(this.currentProgress * 100);
        if (this.currentLegacy > 0) {
            this.progressText.setText(`Next: ${pct}%`);
        } else if (this.targetProgress > 0) {
            this.progressText.setText(`${pct}%`);
        } else {
            this.progressText.setText('');
        }
    }

    destroy(): void {
        this.barBg.destroy();
        this.barFill.destroy();
        this.shimmer.destroy();
        this.labelText.destroy();
        this.progressText.destroy();
    }
}
